import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Package,
  Calendar,
  TrendingUp,
  AlertTriangle,
  ImageOff,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import catalog from "@/api/catalog";

function fmt$(n) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  });
}

function shortDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}

export default function CatalogDetail() {
  const { sku } = useParams();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      catalog.product(sku),
      catalog.productTransactions(sku, 200).catch(() => ({ data: [] })),
    ])
      .then(([p, tx]) => {
        if (!alive) return;
        setProduct(p.data);
        setTransactions(tx.data || []);
      })
      .catch(() => alive && setProduct(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [sku]);

  if (loading) {
    return (
      <div className="rounded-lg border border-border p-12 text-center text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t("common.back")}
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {t("catalog.detail.notFound")}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Monthly tx aggregation for chart.
  const byMonth = {};
  for (const tx of transactions) {
    if (!tx.timestamp) continue;
    const k = tx.timestamp.slice(0, 7);
    byMonth[k] = (byMonth[k] || 0) + 1;
  }
  const monthEntries = Object.entries(byMonth).sort((a, b) =>
    a[0].localeCompare(b[0])
  );
  const maxCount = Math.max(1, ...monthEntries.map((e) => e[1]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t("common.back")}
        </Button>
        {product.handle && (
          <a
            href={`https://${product.handle}.shopify.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:underline"
          >
            Shopify
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Image + key facts */}
        <Card className="lg:col-span-1">
          <CardContent className="space-y-3 p-3">
            <div className="aspect-square w-full overflow-hidden rounded-md bg-muted">
              {product.image_url && !imgErr ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  onError={() => setImgErr(true)}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <ImageOff className="h-16 w-16 text-muted-foreground/30" />
                </div>
              )}
            </div>
            <div>
              <div className="font-mono text-xs text-muted-foreground">
                {product.sku}
              </div>
              <h2 className="mt-1 text-lg font-semibold">{product.name || "—"}</h2>
              {product.vendor && (
                <div className="text-xs text-muted-foreground">
                  {product.vendor}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {product.is_dead && (
                <Badge className="bg-red-900/80 text-red-100">
                  {t("catalog.badges.dead")}
                </Badge>
              )}
              {product.is_no_sales && !product.is_dead && (
                <Badge className="bg-red-500/80 text-white">
                  {t("catalog.badges.noSales")}
                </Badge>
              )}
              {product.is_old && (
                <Badge className="bg-amber-500/80 text-white">
                  {t("catalog.badges.old")}
                </Badge>
              )}
              {product.no_stock && (
                <Badge className="bg-slate-600/80 text-slate-100">
                  {t("catalog.badges.noStock")}
                </Badge>
              )}
              {product.qty_as_is > 0 && (
                <Badge variant="outline" className="border-slate-500/60 text-slate-300">
                  {t("catalog.badges.asIs", { n: product.qty_as_is })}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Numbers */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">{t("catalog.detail.numbers")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label={t("catalog.card.retail")} value={fmt$(product.retail_price)} />
              <Stat label={t("catalog.card.cost")} value={fmt$(product.unit_cost)} />
              <Stat
                label={t("catalog.card.totalMarkup")}
                value={fmt$(product.total_plus_markup)}
              />
              <Stat
                label={t("catalog.detail.margin")}
                value={
                  product.margin_pct !== null && product.margin_pct !== undefined
                    ? `${product.margin_pct.toFixed(1)}%`
                    : "—"
                }
              />
              <Stat
                label={t("catalog.card.inStock")}
                value={product.qty_in_stock?.toLocaleString() || "0"}
              />
              <Stat
                label={t("catalog.card.capitalTied")}
                value={fmt$(product.capital_tied_up)}
                tone="danger"
              />
              <Stat
                label={t("catalog.detail.lastReceived")}
                value={shortDate(product.last_received_date)}
              />
              <Stat
                label={t("catalog.detail.daysInStock")}
                value={product.days_in_stock?.toString() || "—"}
              />
              <Stat
                label={t("catalog.detail.lastTransaction")}
                value={shortDate(product.last_transaction_date)}
              />
              <Stat
                label={t("catalog.detail.daysSinceTx")}
                value={
                  product.days_since_last_transaction !== null
                    ? `${product.days_since_last_transaction}`
                    : "—"
                }
              />
              <Stat
                label={t("catalog.detail.totalTx")}
                value={product.total_transactions?.toLocaleString() || "0"}
              />
              <Stat
                label={t("catalog.detail.category")}
                value={product.category || product.product_type || "—"}
              />
            </div>

            {product.body_html_short && (
              <p className="mt-3 text-xs text-muted-foreground">
                {product.body_html_short}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tx by month chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            <TrendingUp className="mr-1 inline h-3 w-3 text-emerald-500" />
            {t("catalog.detail.monthlyMovement")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monthEntries.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              {t("catalog.detail.noMovement")}
            </div>
          ) : (
            <div className="space-y-1">
              {monthEntries.map(([m, n]) => (
                <div key={m} className="flex items-center gap-2 text-[11px]">
                  <span className="w-16 font-mono text-muted-foreground">{m}</span>
                  <div className="flex-1 overflow-hidden rounded bg-muted">
                    <div
                      className="h-3 rounded bg-primary/60"
                      style={{ width: `${(n / maxCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-10 text-right font-mono">{n}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tx timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            <Calendar className="mr-1 inline h-3 w-3" />
            {t("catalog.detail.timeline", { n: transactions.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-96 overflow-y-auto p-0">
          {transactions.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              {t("catalog.detail.noTx")}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left">{t("catalog.detail.date")}</th>
                  <th className="px-3 py-1.5 text-left">{t("catalog.detail.type")}</th>
                  <th className="px-3 py-1.5 text-left">{t("catalog.detail.employee")}</th>
                  <th className="px-3 py-1.5 text-right">Qty</th>
                  <th className="px-3 py-1.5 text-left">Order</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-border/40 hover:bg-muted/20">
                    <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground">
                      {shortDate(tx.timestamp)}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge
                        variant="outline"
                        className="text-[9px] uppercase tracking-wider"
                      >
                        {tx.transaction_type}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5">
                      {tx.employee_name || tx.employee_id}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{tx.qty}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground">
                      {tx.order_id || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const cls = {
    default: "border-border bg-card",
    danger: "border-red-500/40 bg-red-500/5",
  }[tone];
  return (
    <div className={`rounded-md border p-2 ${cls}`}>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
